import React, { FC, useCallback, useEffect, useRef } from "react";
import Article from "../models/menu/Article";
import { getArticleById } from "../models/menu/Menu";
import SlideUpTransition from "../../components/SlideUpTransition";
import { useDialog } from "../dialog/DialogProvider";
import useMenuFilters from "../menu/filters/useMenuFilters";
import store, { useAppDispatch, useAppSelector } from "../redux/store";
import QrArticleSuggestionPage from "../../../qr/pages/ArticleSuggestionPage/ArticleSuggestionPage";
import useIsWidescreen from "../useIsWidescreen";
import { selectArticlesMap } from "../redux/selectors/selectArticlesMap";
import _ from "lodash";
import { OneTimeUpsellWhere } from "../redux/globalSlice";
import { oneTimeUpsellShown } from "../redux/upsellSlice";
import isKiosk from "../helpers/isKiosk.tsx";
import KioskArticleSuggestionPage, {
  ArticleSuggestionPageProps,
} from "../../../kiosk/pages/ArticleSuggestionPage/ArticleSuggestionPage.tsx";
import { BlockUserInput } from "@jamezz/react-components";
import { differenceInMilliseconds } from "date-fns";
import { retrieveValidArticles } from "../../domains/upsells/hooks/useOpenUpsellPage.tsx";
import { v7 } from "uuid";
import { useLazyGetUpsellSuggestionsQuery } from "../upsells/api/upsellsApi.tsx";

export default function useOpenOneTimeUpsellDialog() {
  const { openDialog } = useDialog();
  const articleFilter = useMenuFilters();
  const isWidescreen = useIsWidescreen();

  const vatGroup = useAppSelector((state) => state.global?.sessionState?.vatGroup);
  const upsellOnOrderMode = useAppSelector(
    (state) => state.global?.salesarea?.upsellSettings?.oneTimeUpsell?.upsell_on_order_mode ?? "both"
  );
  const [fetch] = useLazyGetUpsellSuggestionsQuery();
  const loadingUuid = useRef("");

  useEffect(() => {
    loadingUuid.current = v7().toString();
    return () => {
      console.log("STOPPED 4");
    };
  }, []);

  return useCallback(
    (ArticleSuggestionPage: FC<ArticleSuggestionPageProps>) => {
      return new Promise<void>((resolve) => {
        const state = store.getState();
        const articlesMap = selectArticlesMap(state);
        const upsellSettings = state.global.salesarea.upsellSettings;
        let pageIsOpened = false;

        if (
          upsellSettings &&
          upsellSettings.is_active &&
          upsellSettings.oneTimeUpsell &&
          upsellSettings.oneTimeUpsell.isActive &&
          !_.isEmpty(articlesMap)
        ) {
          if (
            (store.getState().global.salesarea.custom_data?.ai?.use_one_time_upsell_suggestion_api ?? false) &&
            ((vatGroup === 1 && upsellOnOrderMode === "eatIn") ||
              (vatGroup === 2 && upsellOnOrderMode === "takeAway") ||
              upsellOnOrderMode === "both")
          ) {
            const time = new Date();
            const uuid = loadingUuid.current;
            BlockUserInput.blockingStarted(uuid);

            setTimeout(() => {
              BlockUserInput.blockingStopped(uuid);
              // console.log("STOPPED 1");
            }, 5000);

            fetch(
              store.getState().shoppingCart.items.map((item) => ({ name: item.article.name, quantity: item.count }))
            )
              .then((response) => {
                BlockUserInput.blockingStopped(uuid);
                // console.log("STOPPED 2");
                const newTime = new Date();

                if (differenceInMilliseconds(newTime, time) <= 5000 && response.data?.data) {
                  const articles = retrieveValidArticles(
                    articleFilter,
                    response.data.data.map((item) => String(item.id))
                  );

                  if (articles.length > 0) {
                    openDialog({
                      children: (
                        <ArticleSuggestionPage
                          articles={articles}
                          upsellDetails={{ upsellType: "AiUpsell", upsellSource: "OneTimeUpsell" }}
                          // title={action.titleTranslations?.[lang] ?? action.title}
                          // text={action.descriptionTranslations?.[lang] ?? action.description}
                          useAsDialog={true}
                          showBottomHideButton={true}
                        />
                      ),
                      fullScreen: isKiosk() ? "almostFullScreen" : !isWidescreen,
                      TransitionComponent: SlideUpTransition,
                    });
                  }
                }
              })
              .finally(() => {
                BlockUserInput.blockingStopped(uuid);
                // console.log("STOPPED 3");
              });
          } else {
            const productIds = upsellSettings.oneTimeUpsell.productIds;
            if (productIds) {
              const articles: Article[] = JSON.parse(productIds)
                .map((productId: number) => getArticleById(articlesMap, String(productId)))
                .filter(Boolean)
                .filter(articleFilter);
              const validArticlesCount = articles.filter((article) => {
                return !(article.isOutOfStock || article.isNotAvailable);
              }).length;

              if (
                (vatGroup === 1 && upsellOnOrderMode === "eatIn") ||
                (vatGroup === 2 && upsellOnOrderMode === "takeAway") ||
                upsellOnOrderMode === "both"
              ) {
                if (validArticlesCount > 0) {
                  pageIsOpened = true;
                  openDialog({
                    fullScreen: isKiosk() ? "almostFullScreen" : !isWidescreen,
                    children: (
                      <ArticleSuggestionPage
                        articles={articles}
                        title={upsellSettings.oneTimeUpsell.title}
                        text={upsellSettings.oneTimeUpsell.text}
                        upsellDetails={{ upsellType: "OneTimeUpsell" }}
                        translations={upsellSettings.oneTimeUpsell.translations}
                        onClose={resolve}
                      />
                    ),
                    TransitionComponent: SlideUpTransition,
                  });
                }
              }
            }
          }
        }
        if (!pageIsOpened) {
          resolve();
        }
      });
    },
    [fetch, articleFilter, openDialog, isWidescreen, vatGroup, upsellOnOrderMode]
  );
}

export function OnetimeUpsell({ where }: { where: OneTimeUpsellWhere }) {
  const openUpsell = useOpenOneTimeUpsellDialog();
  const shownAt = useAppSelector((state) => state.global.salesarea.upsellSettings?.oneTimeUpsell?.shownAt);
  const articlesMap = useAppSelector(selectArticlesMap);
  const isWidescreen = useIsWidescreen();
  const dispatch = useAppDispatch();
  const dontShow = useAppSelector((state) => state.upsell.oneTimeUpsellShown);

  useEffect(() => {
    if (shownAt) {
      const widescreenOnShoppingCart = shownAt === "onOpenShoppingCart" && isWidescreen;

      if (where === shownAt && !widescreenOnShoppingCart && !_.isEmpty(articlesMap) && !dontShow) {
        dispatch(oneTimeUpsellShown());
        openUpsell(isKiosk() ? KioskArticleSuggestionPage : QrArticleSuggestionPage);
      }
    }
  }, [articlesMap, dispatch, dontShow, isWidescreen, openUpsell, shownAt, where]);

  return null;
}
